import { Controller } from "@hotwired/stimulus";
import "./component.scss";

// Global registry to track active video players
window.communityCardVideoPlayers = window.communityCardVideoPlayers || [];

export default class extends Controller {
  static targets = [
    "imageContainer",
    "videoOverlay",
    "videoIframe",
    "muteButton",
    "muteIcon",
    "communityLink",
  ];
  static values = { hasTrailer: Boolean };

  connect() {
    if (this.hasTrailerValue && this.hasVideoOverlayTarget) {
      this.bindHoverEvents();
    }

    this.player = null;
    this.isMuted = true;
    this.isVideoPlaying = false;

    // Stop all videos immediately when component connects
    this.forceStopAllVideos();

    // Register this instance in global registry
    window.communityCardVideoPlayers.push(this);

    // Listen for Turbo navigation events to rebind events
    this.handleTurboNavigation = this.handleTurboNavigation.bind(this);
    document.addEventListener("turbo:render", this.handleTurboNavigation);
    document.addEventListener("turbo:load", this.handleTurboNavigation);
  }

  disconnect() {
    this.unbindHoverEvents();

    // Remove Turbo event listeners
    if (this.handleTurboNavigation) {
      document.removeEventListener("turbo:render", this.handleTurboNavigation);
      document.removeEventListener("turbo:load", this.handleTurboNavigation);
    }

    // Clean up player instance safely
    if (this.player && this.hasVideoIframeTarget) {
      try {
        // Only pause if iframe is still connected to DOM
        if (this.videoIframeTarget.isConnected) {
          this.player.pause();
        }
      } catch (error) {
        // Silently ignore player errors during disconnect
      }
      this.player = null;
    }

    // Remove this instance from global registry
    const index = window.communityCardVideoPlayers.indexOf(this);
    if (index > -1) {
      window.communityCardVideoPlayers.splice(index, 1);
    }

    // Clean up global registry if empty
    if (window.communityCardVideoPlayers.length === 0) {
      window.communityCardVideoPlayers = [];
    }
  }

  handleTurboNavigation() {
    // Rebind hover events after Turbo navigation
    if (this.hasTrailerValue && this.hasVideoOverlayTarget) {
      this.unbindHoverEvents();
      this.bindHoverEvents();
    }

    // Stop any playing videos after navigation
    this.forceStopAllVideos();
  }

  bindHoverEvents() {
    if (window.matchMedia("(hover: hover)").matches) {
      this.boundOnMouseEnter = this.onMouseEnter.bind(this);
      this.boundOnMouseLeave = this.onMouseLeave.bind(this);

      this.imageContainerTarget.addEventListener(
        "mouseenter",
        this.boundOnMouseEnter
      );
      this.imageContainerTarget.addEventListener(
        "mouseleave",
        this.boundOnMouseLeave
      );
    }
  }

  unbindHoverEvents() {
    if (this.boundOnMouseEnter && this.boundOnMouseLeave) {
      this.imageContainerTarget.removeEventListener(
        "mouseenter",
        this.boundOnMouseEnter
      );
      this.imageContainerTarget.removeEventListener(
        "mouseleave",
        this.boundOnMouseLeave
      );
    }
  }

  forceStopAllVideos() {
    // Reset this component's video state
    if (this.hasVideoOverlayTarget) {
      this.videoOverlayTarget.style.opacity = "0";
      this.videoOverlayTarget.style.visibility = "hidden";
    }

    if (this.hasVideoIframeTarget) {
      this.clearVideoIframe(this.videoIframeTarget);
    }

    this.isVideoPlaying = false;
    this.element.classList.remove(
      "c-community_card-community-card__video-playing"
    );

    // Stop all other video instances too
    window.communityCardVideoPlayers.forEach((controller) => {
      if (controller.hasVideoOverlayTarget) {
        controller.videoOverlayTarget.style.opacity = "0";
        controller.videoOverlayTarget.style.visibility = "hidden";
      }
      if (controller.hasVideoIframeTarget) {
        this.clearVideoIframe(controller.videoIframeTarget);
      }
      controller.isVideoPlaying = false;
      controller.element.classList.remove(
        "c-community_card-community-card__video-playing"
      );
    });
  }

  // Only reset a loaded iframe — assigning src to an idle one makes Safari
  // push a history entry for the current page (one per card on load).
  clearVideoIframe(iframe) {
    if (!iframe) return;

    const current = iframe.getAttribute("src");
    if (current && current !== "about:blank") {
      iframe.src = "about:blank";
    }
  }

  onMouseEnter() {
    this.showVideo();
    // On desktop, video starts playing on hover
    this.isVideoPlaying = true;
    this.element.classList.add(
      "c-community_card-community-card__video-playing"
    );
  }

  onMouseLeave() {
    this.hideVideo();
  }

  showVideo() {
    if (!this.hasVideoOverlayTarget || !this.hasVideoIframeTarget) return;

    try {
      const videoUrl = this.videoIframeTarget.dataset.videoUrl;

      if (videoUrl && videoUrl.trim() !== "") {
        if (this.videoIframeTarget.src !== videoUrl) {
          this.videoIframeTarget.src = videoUrl;
          this.videoIframeTarget.onload = () => {
            this.initializePlayer();
          };
          this.videoIframeTarget.onerror = () => {
            console.warn("Failed to load video iframe");
          };
        } else if (this.player) {
          this.playAndUnmute();
        } else {
          this.initializePlayer();
        }
      }

      this.videoOverlayTarget.style.opacity = "1";
      this.videoOverlayTarget.style.visibility = "visible";
    } catch (error) {
      console.warn("Error showing video:", error);
    }
  }

  hideVideo() {
    if (!this.hasVideoOverlayTarget || !this.hasVideoIframeTarget) return;

    if (this.player) {
      this.player.pause();
    }

    this.videoOverlayTarget.style.opacity = "0";
    this.videoOverlayTarget.style.visibility = "hidden";
    // Reset overlay click protection and ensure it's clickable again
    this.videoOverlayTarget.style.pointerEvents = "auto";

    this.isVideoPlaying = false;
    this.element.classList.remove(
      "c-community_card-community-card__video-playing"
    );
  }

  initializePlayer() {
    if (!window.playerjs?.Player) {
      console.warn("playerjs library not available");
      return;
    }

    try {
      this.player = new playerjs.Player(this.videoIframeTarget);
      this.player.on("ready", () => {
        this.playAndUnmute();
      });
      this.player.on("error", (error) => {
        console.warn("Player error:", error);
      });
    } catch (error) {
      console.warn("Failed to initialize player:", error);
    }
  }

  playAndUnmute() {
    if (!this.player) return;

    try {
      if (this.player.supports("method", "mute")) {
        this.player.mute();
        this.isMuted = true;
        this.updateMuteButton();
      }

      if (this.player.supports("method", "play")) {
        this.player.play();
      }
    } catch (error) {
      console.warn("Error during video play:", error);
    }
  }

  toggleMute(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.player) return;

    try {
      if (this.isMuted) {
        if (this.player.supports("method", "unmute")) {
          this.player.unmute();
          this.isMuted = false;
        }
      } else {
        if (this.player.supports("method", "mute")) {
          this.player.mute();
          this.isMuted = true;
        }
      }

      this.updateMuteButton();
    } catch (error) {
      console.warn("Error toggling mute:", error);
    }
  }

  updateMuteButton() {
    if (this.hasMuteIconTarget) {
      this.muteIconTarget.textContent = this.isMuted ? "🔇" : "🔊";
    }
  }

  handleVideoClick(event) {
    if (event.target.closest('[data-community-card-target="muteButton"]')) {
      return;
    }

    this.navigateToCommunity();
  }

  navigateToCommunity() {
    if (this.hasCommunityLinkTarget) {
      const clickEvent = new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: true,
        detail: { fromVideoOverlay: true },
      });
      this.communityLinkTarget.dispatchEvent(clickEvent);
    }
  }

  pauseAllOtherVideos() {
    window.communityCardVideoPlayers.forEach((controller) => {
      if (
        controller !== this &&
        controller.hasVideoOverlayTarget &&
        controller.videoOverlayTarget.style.opacity === "1"
      ) {
        controller.hideVideo();
      }
    });
  }
}
